import { useAtom, useAtomValue } from 'jotai';
import {
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Spinner } from '@optiaxiom/react';

import { spotlightEnabledAtom, spotlightOpenAtom } from '@frontend/atoms';
import {
  type RenderableAction,
  spotlightDefaultModeAtom,
  spotlightModesAtom,
  spotlightModeStackAtom,
  spotlightModifiersAtom,
  spotlightQueryAtom,
  type TCtx,
  type TModifiers,
  useSpotlightAPI,
  useSpotlightView,
} from '@frontend/components/Spotlight/store';
import {
  SpotlightContent,
  SpotlightGroup,
  SpotlightInput,
  SpotlightItem,
  SpotlightList,
  SpotlightRoot,
} from '@frontend/components/ui/spotlight';
import { useSpotlightModifiers } from '@frontend/hooks/spotlight';

const modifiersFromEvent = (e: KeyboardEvent | MouseEvent): TModifiers => ({
  cmd: e.metaKey,
  ctrl: e.ctrlKey,
  shift: e.shiftKey,
  opt: e.altKey,
});

// Stable ids wiring the combobox input to its listbox + active option, so screen
// readers announce the keyboard selection (which is virtual — DOM focus stays in
// the input). Options are keyed by their flat index.
const LISTBOX_ID = 'spotlight-listbox';
const optionId = (index: number) => `spotlight-option-${index}`;

/**
 * Global Spotlight host — mounted once in the app shell. Renders nothing until a
 * consumer opts in via `useSpotlight`. Drives the vendored primitive from the
 * registry view: renders grouped/sorted results, handles keyboard nav
 * (arrows/enter), backspace-to-pop-mode, live modifier tracking, and async
 * `execute` (spinner until settle, keep-open on `true`).
 */
export function Spotlight() {
  const enabled = useAtomValue(spotlightEnabledAtom);
  const [open, setOpen] = useAtom(spotlightOpenAtom);
  const [query, setQuery] = useAtom(spotlightQueryAtom);
  const modeStack = useAtomValue(spotlightModeStackAtom);
  const defaultMode = useAtomValue(spotlightDefaultModeAtom);
  const modes = useAtomValue(spotlightModesAtom);
  const modifiers = useAtomValue(spotlightModifiersAtom);
  const api = useSpotlightAPI();
  const view = useSpotlightView();

  const [rawFocused, setRawFocused] = useState(0);
  const [pending, setPending] = useState(false);
  // Bumped whenever the Spotlight closes/disables (below). An async `execute`
  // captures the value at dispatch and no-ops its settlement if it changed — so a
  // promise resolving after a close→reopen can't act on the new session.
  const sessionRef = useRef(0);

  useSpotlightModifiers(enabled && open);

  // Reset transient UI and return the mode path to the default whenever the
  // Spotlight is closed or disabled (so the next open always starts fresh).
  // Because this also runs while closed, it seeds the path from the default on
  // mount and whenever the default changes.
  useEffect(() => {
    if (!enabled || !open) {
      sessionRef.current += 1;
      setQuery('');
      setRawFocused(0);
      setPending(false);
      api.setMode(defaultMode);
    }
  }, [enabled, open, defaultMode, setQuery, api]);

  // Clear the query and refocus the top result when the mode changes.
  useEffect(() => {
    setQuery('');
    setRawFocused(0);
  }, [modeStack, setQuery]);

  // Refocus the top result as the query narrows the list.
  useEffect(() => setRawFocused(0), [query]);

  // Focus is intentionally NOT reset when modifiers change (the view also recomputes
  // on them): resetting would yank focus to the top while the user holds Cmd/Ctrl.
  // Safe only while modifiers are render-hints (e.g. the new-tab hint) and never
  // reorder or hide rows. If an action's `hidden`/`label` ever becomes
  // modifier-dependent, switch focus tracking from index to reactKey.

  // Clamp the focused index to the current results at render time (not in a post-paint
  // effect), so a query that shrinks the list never leaves the highlight on a stale row
  // for a frame. Arrow handlers advance from this clamped `focused` (not rawFocused), so
  // they stay adjacent even if the list shrank while open without a query/mode change.
  const focused = view.flat.length ? Math.min(rawFocused, view.flat.length - 1) : 0;
  const focusedItem = view.flat[focused];
  const focusedKey = focusedItem?.reactKey;
  const optionIdByKey = new Map(view.flat.map((item, i) => [item.reactKey, optionId(i)]));
  // An empty result set renders as a status message, not a listbox, so the combobox
  // must not advertise an expanded listbox popup (or control a non-listbox element).
  const hasResults = view.flat.length > 0;

  // Keep the keyboard-focused row visible as arrow-nav moves past the viewport
  // edges (incl. wrap-around from last→first). Ref is bound only to the focused
  // row, so it points at the new node by the time this effect runs. `focusedKey`
  // changes only from keyboard nav / query narrowing (hover no longer sets it),
  // so the mouse never triggers a scroll.
  const focusedRowRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    focusedRowRef.current?.scrollIntoView({ block: 'nearest' });
  }, [focusedKey]);

  // Focus lifecycle: fire the focused action's onFocus, and its onBlur when focus
  // moves away or the Spotlight closes. A ref holds the latest item so this runs
  // only on focus/open change, not on every view recompute.
  const focusedItemRef = useRef(focusedItem);
  focusedItemRef.current = focusedItem;
  useEffect(() => {
    if (!enabled || !open) return;
    const item = focusedItemRef.current;
    item?.action.onFocus?.(item.ctx, api);
    return () => item?.action.onBlur?.(item.ctx, api);
  }, [focusedKey, enabled, open, api]);

  const runAction = useCallback(
    (item: RenderableAction, mods: TModifiers) => {
      // Re-entrancy guard: ignore a second Enter/click while an async execute is
      // pending, so a non-idempotent command can't be double-run.
      if (pending) return;
      const ctx: TCtx = { modePath: view.modePath, query, modifiers: mods, data: item.action.data };
      let result;
      try {
        result = item.action.execute(ctx, api);
      } catch (err) {
        console.error(err);
        return;
      }
      if (result instanceof Promise) {
        // Guard the settlement against a close→reopen: only act if still the same session.
        const epoch = sessionRef.current;
        setPending(true);
        result
          .then(keepOpen => {
            if (sessionRef.current === epoch && keepOpen !== true) api.close();
          })
          .catch(err => console.error(err))
          .finally(() => {
            if (sessionRef.current === epoch) setPending(false);
          });
      } else if (result !== true) {
        api.close();
      }
    },
    [api, view.modePath, query, pending]
  );

  const handleKeyDown = (e: KeyboardEvent) => {
    // Ignore keys that commit an IME composition — the Enter that confirms CJK/IME
    // text also fires keydown, and acting on it would execute the focused action
    // mid-composition instead of just confirming the text. Some IMEs / older Safari
    // report the commit key with keyCode 229 and isComposing already false, so guard
    // on both.
    if (e.nativeEvent.isComposing || e.nativeEvent.keyCode === 229) return;
    const len = view.flat.length;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (len) setRawFocused((focused + 1) % len);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (len) setRawFocused((focused - 1 + len) % len);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = view.flat[focused];
      if (item) runAction(item, modifiersFromEvent(e));
    } else if (e.key === 'Backspace' && query === '' && modeStack.length > 0) {
      e.preventDefault();
      api.popMode(1);
    }
  };

  if (!enabled) return null;

  // Breadcrumb: for each frame of the current path, render the prefix of the mode
  // registered at that exact cumulative path (if any). Root `/` is a normal mode.
  const baseCtx: TCtx = { modePath: view.modePath, query, modifiers, data: undefined };
  const modeChips: ReactNode[] = [];
  const segments: string[] = [];
  view.modePath.forEach(frame => {
    if (frame.segment !== null) segments.push(frame.segment);
    const path = frame.segment === null ? '/' : `/${segments.join('/')}`;
    const mode = modes[path];
    if (!mode?.prefixRender) return;
    modeChips.push(
      <span key={path}>
        {mode.prefixRender({
          ctx: { ...baseCtx, mode: { path, segments: [...segments], data: frame.data } },
        })}
      </span>
    );
  });
  // When a fallback is active, its leading adornment (e.g. the Opal orb) replaces
  // both the search icon (root) and the mode prefix chips (in a mode).
  const addonBefore = view.fallbackActive ? (
    <span style={{ display: 'flex', alignItems: 'center', paddingInlineStart: 2 }}>
      {view.inputAdornment}
    </span>
  ) : modeChips.length ? (
    <span
      style={{
        display: 'flex',
        alignSelf: 'stretch',
        alignItems: 'center',
        gap: 4,
        paddingBlock: 8,
      }}
    >
      {modeChips}
    </span>
  ) : undefined;

  return (
    <SpotlightRoot open={open} onOpenChange={setOpen}>
      <SpotlightContent onKeyDown={handleKeyDown}>
        <SpotlightInput
          addonAfter={pending ? <Spinner /> : undefined}
          addonBefore={addonBefore}
          aria-activedescendant={focusedItem ? optionId(focused) : undefined}
          aria-autocomplete="list"
          aria-controls={hasResults ? LISTBOX_ID : undefined}
          aria-expanded={open && hasResults}
          aria-haspopup="listbox"
          aria-label="Search commands"
          onValueChange={setQuery}
          placeholder="Type a command…"
          role="combobox"
          value={query}
        />
        <SpotlightList aria-label="Results" empty="No results" id={LISTBOX_ID}>
          {view.groups.map(group => (
            <SpotlightGroup key={group.group.key} label={group.group.label}>
              {group.items.map(item => (
                <SpotlightItem
                  focused={item.reactKey === focusedKey}
                  id={optionIdByKey.get(item.reactKey)}
                  key={item.reactKey}
                  ref={item.reactKey === focusedKey ? focusedRowRef : undefined}
                  onClick={e => runAction(item, modifiersFromEvent(e))}
                >
                  {item.render({
                    data: item.action.data,
                    ctx: item.ctx,
                    focused: item.reactKey === focusedKey,
                  })}
                </SpotlightItem>
              ))}
            </SpotlightGroup>
          ))}
        </SpotlightList>
      </SpotlightContent>
    </SpotlightRoot>
  );
}
